import { useEffect } from 'react';
import Advanced from 'components/Advanced';
import _ from 'lodash';
import { fetchAvailabilityRelations } from 'slices/availabilityRelation.slice';
import { fetchFields } from 'slices/field.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import { fetchRelationTypes } from 'slices/relationType.slice';
import { fetchReservationModes } from 'slices/reservationMode.slice';
import {
  advancedReservationTemplatesSelector,
  fetchAdvancedSettingsForReservationTemplate,
} from 'slices/reservationTemplate.slice';
import { fetchRoles } from 'slices/roles.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { fetchTimeRules } from 'slices/timeRule.slice';
import type { KeyValueCallback } from 'types/utils.type';
import type { TReservationTemplate } from '@timeedit/types';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import MoveReservation from './sections/MoveReservation';
import MoveReservationsToTrash from './sections/MoveReservationToTrash';
import ObjectTypesSection from './sections/ObjectTypesSection';
import TemplateAttributes from './sections/TemplateAttributes';
import TemplateCapRes from './sections/TemplateCapRes';
import TemplateInformation from './sections/TemplateInformation';
import TypeCombination from './sections/TypeCombination';
import './ReservationTemplateDrawer.scss';

interface ActionSettings {
  actionId: string;
  name: string;
}

interface ReservationTemplateProps {
  template: TReservationTemplate;
  onChange: KeyValueCallback<TReservationTemplate>;
  isExpand?: boolean;
}

const ReservationTemplate = (props: ReservationTemplateProps) => {
  const { template, onChange, isExpand } = props;
  const advancedSettings: ActionSettings[] = useAppSelector(
    advancedReservationTemplatesSelector(template)
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(fetchTimeRules());
    dispatch(fetchRoles());
    dispatch(fetchOrganizationHierarchy());
    dispatch(fetchRelationTypes());
    dispatch(fetchFields());
    dispatch(fetchReservationModes());
    dispatch(fetchAvailabilityRelations());
  }, [dispatch]);

  useEffect(() => {
    if (template?.id) {
      dispatch(fetchAdvancedSettingsForReservationTemplate(template.id));
    }
  }, [dispatch, template?.id]);

  if (!template) return null;
  return (
    <div className="reservation-template">
      <TEDrawerSection label="General info">
        <TemplateInformation
          template={template}
          onChange={onChange}
          isExpand={isExpand}
        />
      </TEDrawerSection>
      <TEDrawerSection label="Object types">
        <ObjectTypesSection template={template} onChange={onChange} />
        <TypeCombination
          template={template}
          onChange={(combinations: number[][]) =>
            onChange('typeCombinations', combinations)
          }
        />
      </TEDrawerSection>
      <TEDrawerSection label="Attributes">
        <TemplateAttributes
          template={template}
          onChange={onChange}
          isExpand={isExpand}
        />
      </TEDrawerSection>
      <TEDrawerSection label="Move reservations">
        <MoveReservation template={template} />
      </TEDrawerSection>
      <TEDrawerSection label="Move reservations to trash">
        <MoveReservationsToTrash template={template} />
      </TEDrawerSection>
      <TEDrawerSection label="Advanced">
        <Advanced
          options={advancedSettings}
          selectedUrl={'reservation-templates'}
          selectedId={template && template.id}
        />
      </TEDrawerSection>
      <TEDrawerSection label="Capacity/Size reservation">
        <TemplateCapRes template={template} onChange={onChange} />
      </TEDrawerSection>
      <TEDrawerSection label="Comments">
        <div>
          <TEInput.TextArea
            value={_.get(template, 'documentation', '')}
            onChange={(val) => onChange('documentation', val)}
            className="textarea"
          />
        </div>
      </TEDrawerSection>
    </div>
  );
};

export default ReservationTemplate;
